import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import type { Listing } from '@shared/types';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getListingPath } from '../lib/listingPaths';
import { normalizeCity } from '../lib/areaSlug';
import {
  formatListingLocationLine,
  getListingAreaGroupKey,
  getListingAreaGroupLabel,
  listingMatchesBrowseCity,
} from '../lib/locationRegions';
import { stripHtml } from '../lib/content';
import { usePublishedListings } from '../hooks/usePublishedListings';

const mapContainerStyle = {
  width: '100%',
  height: '600px',
};

const defaultCenter = {
  lat: -25.7479,
  lng: 28.2293,
};

const libraries: ('places' | 'geometry')[] = ['places', 'geometry'];

/**
 * Outer shell: no useLoadScript until a real key exists.
 * Passing '' into useLoadScript makes the library load js?key=demo-key → InvalidKey errors.
 */
export default function FindProperty() {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!googleMapsApiKey) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">
            Google Maps API key is not configured. Add{' '}
            <code className="bg-yellow-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> to{' '}
            <code className="bg-yellow-100 px-1 rounded">apps/frontend/.env.production</code> and
            rebuild.
          </p>
        </div>
      </div>
    );
  }
  return <FindPropertyMap googleMapsApiKey={googleMapsApiKey} />;
}

function FindPropertyMap({ googleMapsApiKey }: { googleMapsApiKey: string }) {
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(6);
  const [activeCity, setActiveCity] = useState<string>('');
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });

  const { data: listings, isLoading } = usePublishedListings();

  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          setMapCenter(location);
          setMapZoom(12);
        },
        () => {
          toast.error('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser.');
    }
  }, []);

  useEffect(() => {
    if (userLocation && listings && listings.length > 0) {
      setMapCenter(userLocation);
    }
  }, [userLocation, listings]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMarkerClick = (listing: Listing) => {
    setSelectedListing(listing);
  };

  const getDistance = (
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ) => {
    const R = 6371;
    const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
    const dLon = ((point2.lng - point1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((point1.lat * Math.PI) / 180) *
        Math.cos((point2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const sortedListings =
    userLocation && listings
      ? [...listings].sort((a, b) => {
          const distA = getDistance(userLocation, a.location);
          const distB = getDistance(userLocation, b.location);
          return distA - distB;
        })
      : listings || [];

  const availableCityTabs = useMemo(() => {
    const src = listings || [];
    const byCity = new Map<string, { label: string; count: number }>();
    for (const l of src) {
      const label = getListingAreaGroupLabel(l);
      const key = getListingAreaGroupKey(l);
      if (!key) continue;
      const prev = byCity.get(key);
      byCity.set(key, { label, count: (prev?.count || 0) + 1 });
    }
    const dynamic = [...byCity.values()]
      .sort((a, b) => b.count - a.count)
      .map((v) => v.label);
    // Dynamic-only: show a city tab only if listings exist for it in the DB.
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const c of dynamic) {
      const k = normalizeCity(c);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      unique.push(c);
    }
    return unique;
  }, [listings]);

  useEffect(() => {
    if (!activeCity) return;
    if (!availableCityTabs.some((c) => normalizeCity(c) === normalizeCity(activeCity))) {
      setActiveCity('');
    }
  }, [availableCityTabs, activeCity]);

  const visibleListings = useMemo(() => {
    const src = sortedListings || [];
    if (!activeCity) return src;
    return src.filter((l) => listingMatchesBrowseCity(l, activeCity));
  }, [sortedListings, activeCity]);

  if (loadError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading Google Maps. Check API key and billing.</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Find a Property</h1>
          <p className="text-gray-600">Browse properties on the map or find one near you</p>
        </div>
        <button
          onClick={getCurrentLocation}
          className="btn btn-primary flex items-center"
        >
          <i className="uil uil-location-point text-base mr-2"></i>
          Find Property Near Me
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-white">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveCity('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      !activeCity
                        ? 'bg-[#32373c] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {availableCityTabs.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => setActiveCity(city)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        normalizeCity(activeCity) === normalizeCity(city)
                          ? 'bg-[#32373c] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </div>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={onMapLoad}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  // mapId enables Advanced Markers later; legacy Marker still works but logs deprecation.
                }}
              >
                {userLocation && (
                  <Marker
                    position={userLocation}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 8,
                      fillColor: '#4285F4',
                      fillOpacity: 1,
                      strokeColor: '#ffffff',
                      strokeWeight: 2,
                    }}
                    title="Your Location"
                  />
                )}

                {visibleListings.map((listing) => {
                  if (!listing.location?.lat || !listing.location?.lng) return null;
                  return (
                    <Marker
                      key={listing.id}
                      position={{
                        lat: listing.location.lat,
                        lng: listing.location.lng,
                      }}
                      onClick={() => handleMarkerClick(listing)}
                      icon={{
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#FF5A5F',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                      }}
                    />
                  );
                })}

                {selectedListing &&
                  selectedListing.location?.lat &&
                  selectedListing.location?.lng && (
                    <InfoWindow
                      position={{
                        lat: selectedListing.location.lat,
                        lng: selectedListing.location.lng,
                      }}
                      onCloseClick={() => setSelectedListing(null)}
                    >
                      <div className="p-2 max-w-xs">
                        {(selectedListing.coverImageUrl || selectedListing.images?.[0]) && (
                          <img
                            src={selectedListing.coverImageUrl || selectedListing.images?.[0]}
                            alt={selectedListing.title}
                            className="w-full h-28 object-cover rounded mb-2 bg-gray-100"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'https://via.placeholder.com/600x400?text=Image+Not+Available';
                            }}
                          />
                        )}
                        <h3 className="font-semibold text-lg mb-1">{selectedListing.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {formatListingLocationLine(selectedListing)}
                        </p>
                        {!!stripHtml(selectedListing.description || '') && (
                          <p className="text-sm text-gray-700 mb-2 line-clamp-3">
                            {stripHtml(selectedListing.description || '')}
                          </p>
                        )}
                        <p className="text-sm font-medium text-[#32373c] mb-2">
                          R {(selectedListing.basePrice / 100).toFixed(2)}/night
                        </p>
                        <Link
                          to={getListingPath(selectedListing)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View Details →
                        </Link>
                      </div>
                    </InfoWindow>
                  )}
              </GoogleMap>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-semibold mb-4">
                {activeCity
                  ? `Properties in ${activeCity}`
                  : userLocation
                    ? 'Properties Near You'
                    : 'All Properties'}
              </h2>
              {visibleListings.length > 0 ? (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {visibleListings.map((listing) => {
                    const distance = userLocation
                      ? `${getDistance(userLocation, listing.location).toFixed(1)} km away`
                      : null;
                    const coverImage = listing.coverImageUrl || listing.images?.[0];
                    const cleanDescription = stripHtml(listing.description || '');
                    const locationLine = formatListingLocationLine(listing);
                    return (
                      <Link
                        key={listing.id}
                        to={getListingPath(listing)}
                        className="block p-3 border border-gray-200 rounded-lg hover:border-[#32373c] hover:shadow-md transition-all"
                        onMouseEnter={() => {
                          if (listing.location?.lat && listing.location?.lng) {
                            setMapCenter({
                              lat: listing.location.lat,
                              lng: listing.location.lng,
                            });
                            setMapZoom(14);
                          }
                        }}
                      >
                        <div className="flex gap-3">
                          <div className="shrink-0 w-20 h-16 rounded-md overflow-hidden bg-gray-100 border border-gray-200">
                            {coverImage ? (
                              <img
                                src={coverImage}
                                alt={listing.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    'https://via.placeholder.com/320x240?text=No+Image';
                                }}
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-sm mb-1 line-clamp-1">{listing.title}</h3>
                            <p className="text-xs text-gray-600 mb-1 line-clamp-1">
                              {locationLine}
                            </p>
                            {!!cleanDescription && (
                              <p className="text-xs text-gray-700 mb-2 line-clamp-2">{cleanDescription}</p>
                            )}
                            {distance && (
                              <p className="text-xs text-blue-600 font-medium mb-1">{distance}</p>
                            )}
                            <p className="text-sm font-bold text-[#32373c]">
                              R {(listing.basePrice / 100).toFixed(2)}/night
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-600 text-sm">No properties found</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
